/**
 * useQuickFlowContext — Reads the current quick-flow form state
 *
 * Detects whether the user is on /schnellanfrage or /schnellangebot,
 * reads the current step from URL search params, and reads form data
 * from localStorage (where useFormPersistence saves it).
 *
 * Zero coupling to the form components — reads external state only.
 */

"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import type { QuickFlowType, QuickFlowContext, QuickFlowFormSummary } from "@/lib/chat/types";
import { parseStoredStatement } from "@/lib/requests/axis-hints";
import { funnelBiotopausgleich } from "@/lib/requests/funnel-statement";
import { parentFromOfferingStatement, parseStoredOfferingStatement } from "@/lib/offerings/axis-statement";
import { QUICK_OFFERING_PERSISTENCE_KEY, QUICK_REQUEST_PERSISTENCE_KEY } from "@/lib/quick-flow/types";
// The funnels persist through useFormPersistence's default TTL; a session it no longer serves
// is not read back here either.
import { DEFAULT_TTL } from "@/lib/quick-flow/useFormPersistence";

// ---------------------------------------------------------------------------
// Step definitions — mirrored from QuickRequestV2 / QuickOfferingV2
// ---------------------------------------------------------------------------

const ANFRAGE_STEPS = ["location", "bedarf", "contact"] as const;
const ANGEBOT_STEPS = ["location", "offer", "contact"] as const;

/** A buyer-funnel step, as this hook reports it in `step`. */
export type AnfrageStep = (typeof ANFRAGE_STEPS)[number];

/** A seller-funnel step, as this hook reports it in `step`. */
export type AngebotStep = (typeof ANGEBOT_STEPS)[number];

/**
 * Exported so the chat's step indicator can assert parity against them
 * (`lib/chat/flow-progress.funnel-parity.test.ts`). Klara's capture flow shows
 * the SAME steps as the funnel — a user who abandons the form and asks her
 * instead must not be handed a second, different vocabulary.
 */
export const ANFRAGE_LABELS: Record<AnfrageStep, string> = {
  location: "Standort",
  bedarf: "Bedarf",
  contact: "Kontakt"
};

/** See ANFRAGE_LABELS — exported for the chat step-indicator parity test. */
export const ANGEBOT_LABELS: Record<AngebotStep, string> = {
  location: "Standort",
  offer: "Angebot",
  contact: "Kontakt"
};

/** Slug → internal step name (matches [step]/page.tsx route handlers) */
const ANFRAGE_SLUG_MAP: Record<string, AnfrageStep> = {
  standort: "location",
  bedarf: "bedarf",
  kontakt: "contact"
};
const ANGEBOT_SLUG_MAP: Record<string, AngebotStep> = {
  standort: "location",
  angebot: "offer",
  kontakt: "contact"
};

// ---------------------------------------------------------------------------
// localStorage reading (matches useFormPersistence format)
// ---------------------------------------------------------------------------

interface PersistedData {
  data: Record<string, unknown>;
  timestamp: number;
}

/**
 * Read raw form data from localStorage.
 * Exported so useSalesChat can re-read at send-time for freshest data.
 */
export function readFormDataFromStorage(flow: QuickFlowType, editId?: string | null): Record<string, unknown> | null {
  if (typeof window === "undefined") return null;
  const baseKey = flow === "schnellanfrage" ? QUICK_REQUEST_PERSISTENCE_KEY : QUICK_OFFERING_PERSISTENCE_KEY;
  const key = editId ? `${baseKey}_edit_${editId}` : baseKey;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed: PersistedData = JSON.parse(raw);
    if (Date.now() - parsed.timestamp > DEFAULT_TTL) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

/**
 * Extract a sanitized form summary — domain fields only, no PII.
 * Exported for re-reading at send-time (fresh data from localStorage).
 */
export function extractFormSummary(flow: QuickFlowType, raw: Record<string, unknown>): QuickFlowFormSummary {
  const str = (key: string) => {
    const v = raw[key];
    return typeof v === "string" && v.length > 0 ? v : undefined;
  };
  const base: QuickFlowFormSummary = {
    type: str("type"),
    bundesland: str("bundesland"),
    kreis: str("kreis"),
    naturraum: str("naturraum"),
    quantity: str("quantity")
  };
  if (flow === "schnellanfrage") {
    // The customer's Biotopausgleich, as their statement names it: its product and how much.
    const biotopausgleich = "statement" in raw ? funnelBiotopausgleich(parseStoredStatement(raw.statement)) : undefined;
    return {
      ...base,
      ...(biotopausgleich !== undefined ? {
        type: biotopausgleich?.product,
        quantity: biotopausgleich?.quantity?.toLocaleString("de-DE", {
          maximumFractionDigits: 20
        })
      } : {}),
      maxPricePerPoint: str("maxPricePerPoint")
    };
  }

  // schnellangebot: the seller's Biotopausgleich, read from their statement with the reader the
  // payload uses (`buildOfferingPayload`). Without Ökopunkte or hectares the offer names no
  // denomination and is submitted with no quantity and no price, so none is read back here either.
  const parent = parentFromOfferingStatement(parseStoredOfferingStatement(raw.statement));
  const supply = parent.naturschutz_supply;
  return {
    ...base,
    type: supply ? parent.type : undefined,
    quantity: supply ? parent.quantity?.toLocaleString("de-DE", {
      maximumFractionDigits: 20
    }) : undefined,
    price: supply ? str("price") : undefined,
    hasGeometry: raw.geometry != null && raw.geometry !== ""
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Returns the current quick-flow context, or null if the user is not
 * on a form page.
 *
 * Recomputes when the URL changes (pathname or search params).
 */
export function useQuickFlowContext() {
  const $ = _c(18);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t0;
  bb0: {
    if (!pathname) {
      t0 = null;
      break bb0;
    }
    let flow = null;
    if (pathname.includes("/schnellanfrage")) {
      flow = "schnellanfrage";
    } else {
      if (pathname.includes("/schnellangebot")) {
        flow = "schnellangebot";
      }
    }
    if (!flow) {
      t0 = null;
      break bb0;
    }
    let t1;
    if ($[0] !== pathname) {
      let t2;
      if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = /\/schnell(?:anfrage|angebot)\/([^/]+)/;
        $[2] = t2;
      } else {
        t2 = $[2];
      }
      t1 = pathname.match(t2);
      $[0] = pathname;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    const slugMatch = t1;
    const slug = slugMatch?.[1] ?? "standort";
    let labels;
    let step;
    let stepIndex;
    let steps;
    let t2;
    if ($[3] !== flow || $[4] !== searchParams || $[5] !== slug) {
      const editId = searchParams?.get("editId") ?? null;
      const slugMap = flow === "schnellanfrage" ? ANFRAGE_SLUG_MAP : ANGEBOT_SLUG_MAP;
      steps = flow === "schnellanfrage" ? ANFRAGE_STEPS : ANGEBOT_STEPS;
      labels = flow === "schnellanfrage" ? ANFRAGE_LABELS : ANGEBOT_LABELS;
      step = slugMap[slug] ?? steps[0];
      stepIndex = (steps as readonly string[]).indexOf(step);
      const rawData = readFormDataFromStorage(flow, editId);
      t2 = rawData ? extractFormSummary(flow, rawData) : null;
      $[3] = flow;
      $[4] = searchParams;
      $[5] = slug;
      $[6] = labels;
      $[7] = step;
      $[8] = stepIndex;
      $[9] = steps;
      $[10] = t2;
    } else {
      labels = $[6];
      step = $[7];
      stepIndex = $[8];
      steps = $[9];
      t2 = $[10];
    }
    const formSummary = t2;
    const t3 = labels[step as keyof typeof labels];
    const t4 = stepIndex >= 0 ? stepIndex : 0;
    let t5;
    if ($[11] !== flow || $[12] !== formSummary || $[13] !== step || $[14] !== steps.length || $[15] !== t3 || $[16] !== t4) {
      t5 = {
        flow,
        step,
        stepLabel: t3,
        stepIndex: t4,
        totalSteps: steps.length,
        formSummary,
        isProblematicStep: false
      };
      $[11] = flow;
      $[12] = formSummary;
      $[13] = step;
      $[14] = steps.length;
      $[15] = t3;
      $[16] = t4;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    t0 = t5;
  }
  return t0;
}